import { HttpErrorResponse } from '@angular/common/http';
import { createReducer, on } from '@ngrx/store';
import * as BillingActions from './billing.actions';
import * as _ from 'lodash';
import { Invoice } from '../../@shared/models/billing.model';
import { BillingContract } from '../../@shared/models/billing_contract.model';
import { BillingMetadata } from '../../@shared/models/billing_metadata.model';
import { PartnerInvoice } from '../../@shared/models/partner-invoice.model';
import { PartnerUsage } from '../../@shared/models/partner-usage.model';
import { IMeta, IRequestOptions } from '../request-options.interface';

export const billingFeatureKey = 'billing';

export interface State {
  loaded: boolean;
  loading: boolean;
  invoices: Array<Invoice>;
  error?: Error | HttpErrorResponse;
  address: {
    loading: boolean;
    loaded: boolean;
    data: Partial<BillingAddress>;
  };
  contract: {
    loading: boolean;
    loaded: boolean;
    data: Partial<BillingContract>;
  };
  metadata: {
    loading: boolean;
    loaded: boolean;
    data: Partial<BillingMetadata>;
  };
  partnerInvoices: {
    loading: boolean;
    loadingNext: boolean;
    loaded: boolean;
    data: PartnerInvoice[];
    meta?: IMeta;
    listConfig: IRequestOptions;
  };
  partnerUsage: {
    loading: boolean;
    loaded: boolean;
    data: PartnerUsage[];
  };
}

export interface BillingAddress {
  city: string;
  country: string;
  line_1: string;
  line_2: string;
  name: string;
  phone: string;
  post_code: string;
  state: string;
  billing_email: string;
  vat_id: string;
}

export const initialState: State = {
  loaded: false,
  loading: false,
  invoices: [],
  address: {
    loading: false,
    loaded: false,
    data: {},
  },
  contract: {
    loading: false,
    loaded: false,
    data: {},
  },
  metadata: {
    loading: false,
    loaded: false,
    data: {},
  },
  partnerInvoices: {
    loading: false,
    loadingNext: false,
    loaded: false,
    data: [],
    listConfig: {
      page: 0,
      limit: 20
    }
  },
  partnerUsage: {
    loading: false,
    loaded: false,
    data: [],
  },
};

export const reducer = createReducer(
  initialState,

  on(BillingActions.loadPartnerUsage, (state) => ({
    ...state,
    partnerUsage: {
      ...state.partnerUsage,
      loading: true
    }
  })),

  on(BillingActions.loadPartnerUsageSuccess, (state: State, action) => ({
    ...state,
    partnerUsage: {
      ...state.partnerUsage,
      loaded: true,
      loading: false,
      data: {
        ...action.data,
      },
    },
  })),

  on(BillingActions.loadPartnerUsageFailure, (state, action) => ({
    ...state,
    partnerUsage: {
      ...state.partnerUsage,
      loading: false,
      error: action.error
    }
  })),

  on(BillingActions.loadBillingAddress, (state) => ({
    ...state,
    address: {
      ...state.address,
      loading: true,
    },
  })),

  on(BillingActions.loadBillingAddressSuccess, (state, action) => ({
    ...state,
    address: {
      ...state.address,
      loading: false,
      loaded: true,
      data: {
        ...action.data,
      },
    },
  })),

  on(BillingActions.loadBillingAddressFailure, (state, action) => ({
    ...state,
    address: {
      ...state.address,
      loading: false,
      loaded: true,
      error: action.error,
    },
  })),

  on(BillingActions.loadBillingMetadata, (state) => ({
    ...state,
    metadata: {
      ...state.metadata,
      loading: true,
    },
  })),

  on(BillingActions.loadBillingMetadataSuccess, (state, action) => ({
    ...state,
    metadata: {
      ...state.metadata,
      loading: false,
      loaded: true,
      data: {
        ...action.data,
      },
    },
  })),

  on(BillingActions.loadBillingMetadataFailure, (state, action) => ({
    ...state,
    metadata: {
      ...state.metadata,
      loading: false,
      loaded: true,
      error: action.error,
    },
  })),

  on(BillingActions.loadContract, (state) => ({
    ...state,
    contract: {
      ...state.contract,
      loading: true,
    },
  })),

  on(BillingActions.loadContractSuccess, (state, action) => ({
    ...state,
    contract: {
      ...state.contract,
      loading: false,
      loaded: true,
      data: {
        ...action.data.attributes,
      },
    },
  })),

  on(BillingActions.loadContractFailure, (state, action) => ({
    ...state,
    contract: {
      ...state.contract,
      loading: false,
      loaded: true,
      error: action.error,
    },
  })),

  on(BillingActions.loadPartnerInvoices, (state, action) => ({
    ...state,
    partnerInvoices: {
      ...state.partnerInvoices,
      loading: true,
    },
  })),

  on(BillingActions.loadPartnerInvoicesSuccess, (state, action) => ({
    ...state,
    partnerInvoices: {
      ...state.partnerInvoices,
      loading: false,
      loaded: true,
      data: action.data,
      meta: action.meta
    },
  })),

  on(BillingActions.loadPartnerInvoicesFailure, (state, action) => ({
    ...state,
    partnerInvoices: {
      ...state.partnerInvoices,
      loading: false,
    },
  })),

  on(BillingActions.loadPartnerInvoicesNext, (state, action) => ({
    ...state,

    partnerInvoices: {
      ...state.partnerInvoices,
      loading: true,
      loadingNext: true
    }
  })),

  on(BillingActions.loadPartnerInvoicesNextSuccess, (state, action) => ({
    ...state,

    partnerInvoices: {
      ...state.partnerInvoices,
      data: [...state.partnerInvoices.data, ...action.data],
      meta: action.meta,
      loading: false,
      loadingNext: false,
      loaded: true,
      error: undefined
    }
  })),

  on(BillingActions.loadPartnerInvoicesNextFailure, (state, action) => ({
    ...state,

    partnerInvoices: {
      ...state.partnerInvoices,
      loading: false,
      loadingNext: false,
      loaded: true,
      error: action.error
    }
  })),
);
